import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function Logo() {
  return <Link to="/HomePage" className="z-50 relative inline-block font-mono">
      MOONDOS MEDIA
    </Link>;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return <div data-section-id="23821" className="hidden md:flex h-6 w-64 bg-muted/10 animate-pulse rounded-[2px]" />;
  return <nav className="hidden md:flex items-center gap-8">
      {menuItems.map((item, i) => <div key={item.id} className="relative group" data-index={i}>
          <Link to={item.href || "#"} className="text-[12px] font-normal text-foreground/70 hover:text-foreground transition-colors font-mono uppercase tracking-widest relative py-2 block">
            {item.label}
            {}
            <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-secondary rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out transform translate-y-2 group-hover:translate-y-0" />
          </Link>
        </div>)}
    </nav>;
}
function MobileNav() {
  const {
    menuItems
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  return <div className="md:hidden">
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(true)} className="text-foreground hover:bg-white/5" aria-label="Open menu">
        <Menu className="w-6 h-6" />
      </Button>

      {}
      <div className={`fixed inset-0 z-[60] bg-background/98 backdrop-blur-2xl transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${isOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"}`}>
        <div className="container mx-auto px-6 py-6 h-full flex flex-col">
          <div className="flex justify-between items-center mb-12">
            <Logo />
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="text-foreground hover:bg-white/5" aria-label="Close menu">
              <X className="w-6 h-6" />
            </Button>
          </div>

          <nav className="flex flex-col gap-8 items-center justify-center flex-1">
            {menuItems.map((item, i) => <div key={item.id} data-index={i} className="w-full text-center">
                <Link to={item.href || "#"} className="text-3xl font-normal text-foreground/80 hover:text-primary transition-colors block py-2 font-mono tracking-tight" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>
              </div>)}
            <div className="mt-12 w-full flex justify-center">
              <Button className="w-full max-w-xs text-sm h-14 bg-primary text-primary-foreground font-mono uppercase tracking-widest rounded-[2px]" onClick={() => setIsOpen(false)} asChild>
                <Link to="/StartYourProjectPage">Start a Project</Link>
              </Button>
            </div>
          </nav>
        </div>
      </div>
    </div>;
}
function HeaderContent() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b ${scrolled ? "bg-background/80 backdrop-blur-xl border-white/10 py-3 shadow-lg shadow-black/20" : "bg-background border-transparent py-5"}`}>
      <div className="container mx-auto px-6 flex items-center justify-between">
        <Logo />
        <DesktopNav />
        <MobileNav />
      </div>
    </header>;
}
export default function Header() {
  return <section id="main-navigation" className="dark bg-background text-foreground">
      <MenuProvider menu_id="16" className="w-full">
        <HeaderContent />
      </MenuProvider>
    </section>;
}